import { Button } from "../ui/Button";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { ArrowLeftIcon } from "lucide-react";
import React from "react";

interface PageWrapperProps {
  /**
   * Whether data is currently loading
   */
  isLoading?: boolean;
  /**
   * Label to display while loading
   */
  loadingLabel?: string;
  /**
   * Whether an error occurred while loading
   */
  isError?: boolean;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Callback when the back button is clicked
   */
  onBack?: () => void;
  /**
   * Page title to display after the back button
   */
  title?: string;
  /**
   * Content to render when not loading and no error
   */
  children: React.ReactNode;
  /**
   * Additional className for the container
   */
  className?: string;
}

/**
 * PageWrapper
 * Shared wrapper component for pages that handle loading states, error states,
 * and provide a back button. Useful for registration/edit pages.
 */
export const PageWrapper: React.FC<PageWrapperProps> = ({
  isLoading = false,
  loadingLabel = "Cargando información...",
  isError = false,
  errorMessage = "Error al cargar la información. Por favor, intente nuevamente.",
  onBack,
  title,
  children,
  className = "",
}) => {
  if (isLoading) {
    return (
      <div className={`flex flex-col gap-4 px-3 sm:px-6 py-4 ${className}`}>
        <LoadingSpinner label={loadingLabel} size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`flex flex-col gap-4 px-3 sm:px-6 py-4 ${className}`}>
        {onBack && (
          <Button
            onClick={onBack}
            className="flex items-center gap-2 text-sm sm:text-base max-w-fit"
          >
            <ArrowLeftIcon /> Volver
          </Button>
        )}
        <div className="text-center py-8 text-red-600">{errorMessage}</div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 px-3 sm:px-6 py-4 ${className}`}>
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            onClick={onBack}
            className="flex items-center gap-2 text-sm sm:text-base max-w-fit"
          >
            <ArrowLeftIcon />
          </Button>
        )}
        {title && (
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        )}
      </div>
      {children}
    </div>
  );
};

